import {chain} from 'lodash';

import {reportToBugsnag} from '../../../../srcCommon/services/bugsnag/bugsnag';
import {DropLinePositionsEnum} from '../../../components/inboxes/workspaces/dnd/workspaceDragDropTypes';
import {parseTeamNamespace} from '../../../helpers/models/namespaceHelpers';
import {TagModel} from '../../../models/api/tagModel';
import {TeamModel} from '../../../models/api/teamModel';
import {VisualWorkspaceModel, VisualWorkspaceTag} from '../../../models/api/visualWorkspaceModel';
import {
  RWorkspaceFolderRootLabels,
  WorkspaceFolderLabelV2Model,
  WorkspaceFolderNestedItemV2,
  WorkspaceFolderRootLabelsEnum,
  WorkspaceItemTypesEnum,
  WorkspaceSectionTypesEnum,
} from '../../../models/api/workspaceModel';
import {ThunkAsyncAction} from '../../appThunk';
import {updateShouldAlphabetizeWorkspaceAction} from '../preferences/preferencesActions';
import {getTeams} from '../teams/teamsSelectors';
import {
  WorkspaceDropReorderTeamInboxArgs,
  WorkspaceDropReorderTeammateArgs,
  WorkspaceDropReorderTeammateWorkspaceItemArgs,
  WorkspaceDropReorderTeamViewArgs,
} from './reorder/types';
import {getUserVisualWorkspace} from './visualWorkspace/visualWorkspaceSelectors';
import {
  hideVisualWorkspaceLens,
  reorderVisualWorkspaceDelegation,
  reorderVisualWorkspaceNestedFolder,
  reorderVisualWorkspaceTeamInbox,
  reorderVisualWorkspaceTeammate,
  reorderVisualWorkspaceTeammateWorkspaceTag,
  reorderVisualWorkspaceTeamView,
  showVisualWorkspaceLens,
  toggleVisualWorkspaceRootFolder,
  toggleVisualWorkspaceTeamCustomView,
  toggleVisualWorkspaceTeamInbox,
  toggleVisualWorkspaceTeammate,
} from './visualWorkspace/visualWorkspaceUpdateHelpers';
import {workspaceUpdatedAsyncAction, WorkspaceUpdater} from './workspaceUpdatedAction';

/*
 * Action creators.
 */

/** Moves a private tag or team tag within the personal teammate workspace. */
export function reorderTagInPersonalWorkspaceAsyncAction(
  dropArgs: WorkspaceDropReorderTeammateArgs,
): ThunkAsyncAction {
  return async (dispatch, getState) => {
    const visualWorkspace = getUserVisualWorkspace(getState());
    const updatedVisualWorkspace = reorderVisualWorkspaceTeammateWorkspaceTag(visualWorkspace, dropArgs);
    dispatch(updateShouldAlphabetizeWorkspaceAction(false));
    await dispatch(updateWorkspaceAsyncAction(updatedVisualWorkspace));
  };
}

/** Moves a nested folder item within the personal teammate workspace. */
export function reorderNestedFolderInPersonalWorkspaceAsyncAction(
  dropArgs: WorkspaceDropReorderTeammateWorkspaceItemArgs,
): ThunkAsyncAction {
  return async (dispatch, getState) => {
    const visualWorkspace = getUserVisualWorkspace(getState());
    const updatedVisualWorkspace = reorderVisualWorkspaceNestedFolder(visualWorkspace, dropArgs);
    dispatch(updateShouldAlphabetizeWorkspaceAction(false));
    await dispatch(updateWorkspaceAsyncAction(updatedVisualWorkspace));
  };
}

/** Moves a team custom view within a team workspace. */
export function reorderViewInTeamWorkspaceAsyncAction(
  dropArgs: WorkspaceDropReorderTeamViewArgs,
): ThunkAsyncAction {
  return async (dispatch, getState) => {
    const visualWorkspace = getUserVisualWorkspace(getState());
    const updatedVisualWorkspace = reorderVisualWorkspaceTeamView(visualWorkspace, dropArgs);
    dispatch(updateShouldAlphabetizeWorkspaceAction(false));
    await dispatch(updateWorkspaceAsyncAction(updatedVisualWorkspace));
  };
}

/** Moves a team inbox within a team workspace. */
export function reorderInboxInTeamWorkspaceAsyncAction(
  dropArgs: WorkspaceDropReorderTeamInboxArgs,
): ThunkAsyncAction {
  return async (dispatch, getState) => {
    const visualWorkspace = getUserVisualWorkspace(getState());
    const updatedVisualWorkspace = reorderVisualWorkspaceTeamInbox(visualWorkspace, dropArgs);
    dispatch(updateShouldAlphabetizeWorkspaceAction(false));
    await dispatch(updateWorkspaceAsyncAction(updatedVisualWorkspace));
  };
}

/** Moves a teammate within the assigned teammates workspace. */
export function reorderTeammateInTeammatesWorkspaceAsyncAction(
  dropArgs: WorkspaceDropReorderTeammateArgs,
): ThunkAsyncAction {
  return async (dispatch, getState) => {
    const visualWorkspace = getUserVisualWorkspace(getState());
    const updatedVisualWorkspace = reorderVisualWorkspaceTeammate(visualWorkspace, dropArgs);
    dispatch(updateShouldAlphabetizeWorkspaceAction(false));
    await dispatch(updateWorkspaceAsyncAction(updatedVisualWorkspace));
  };
}

/** Moves an entire delegation workspace--not just an item within it--within the displayed delegation workspaces. */
export function reorderWorkspaceDelegationAsyncAction(
  droppedDelegationId: number,
  targetDelegationId: number,
  linePosition: DropLinePositionsEnum,
): ThunkAsyncAction {
  return async (dispatch, getState) => {
    const visualWorkspace = getUserVisualWorkspace(getState());
    const updatedVisualWorkspace = reorderVisualWorkspaceDelegation(visualWorkspace, {
      droppedItemId: droppedDelegationId,
      targetItemId: targetDelegationId,
      linePosition,
    });
    await dispatch(updateWorkspaceAsyncAction(updatedVisualWorkspace));
  };
}

/** Hides a team inbox in a team workspace. */
function hideInboxInTeamWorkspaceAsyncAction(inboxId: number, teamId: number): ThunkAsyncAction {
  return async (dispatch, getState) => {
    const visualWorkspace = getUserVisualWorkspace(getState());
    const {updatedVisualWorkspace} = toggleVisualWorkspaceTeamInbox(visualWorkspace, {id: inboxId, teamId});
    await dispatch(updateWorkspaceAsyncAction(updatedVisualWorkspace));
  };
}

/**
 * If a team inbox in a team workspace, the item will be hidden.
 * If a private inbox in the personal teammate workspace, no action will be taken because it cannot be hidden.
 */
export function hideWorkspaceInboxAsyncAction(inboxId: number, namespace?: string): ThunkAsyncAction {
  return async (dispatch, getState) => {
    if (!namespace) {
      return undefined;
    }

    const maybeTeamId = parseTeamNamespace(namespace);
    if (maybeTeamId) {
      await dispatch(hideInboxInTeamWorkspaceAsyncAction(inboxId, maybeTeamId));
    }
    return undefined;
  };
}

/** Hides a root folder in the personal teammate workspace. */
export function hidePersonalWorkspaceRootFolderAsyncAction(
  label: WorkspaceFolderRootLabelsEnum,
): ThunkAsyncAction {
  return async (dispatch, getState) => {
    const visualWorkspace = getUserVisualWorkspace(getState());
    const {updatedVisualWorkspace} = toggleVisualWorkspaceRootFolder(visualWorkspace, label);
    await dispatch(updateWorkspaceAsyncAction(updatedVisualWorkspace));
  };
}

/**
 * Hides a private or team tag's visibility in the personal workspace.
 *
 * Ideally, this also explicitly hides any child tags like onToggleTag from @{see WorkspacesEditorProvider},
 * but not doing so does not result in any undesirable behavior because
 * they get implicitly hidden when building the VisualWorkspaceModel regardless.
 */
function hideTagInPersonalWorkspaceAsyncAction(tagId: TagModel['id']): ThunkAsyncAction {
  return async (dispatch, getState) => {
    const visualWorkspace = getUserVisualWorkspace(getState());
    const updatedVisualWorkspace = {
      ...visualWorkspace,
      teammateWorkspaceTags: visualWorkspace.teammateWorkspaceTags.map((item) => {
        if (item.id !== tagId) {
          return item;
        }

        return {
          ...item,
          isSelected: false,
        };
      }),
    };
    await dispatch(updateWorkspaceAsyncAction(updatedVisualWorkspace));
  };
}

/** Shows a private or team tag's visibility in the personal workspace. */
function showTagInPersonalWorkspaceAsyncAction(tagId: TagModel['id']): ThunkAsyncAction {
  return async (dispatch, getState) => {
    const visualWorkspace = getUserVisualWorkspace(getState());
    const updatedVisualWorkspace = {
      ...visualWorkspace,
      teammateWorkspaceTags: showVisualWorkspaceTag(tagId, visualWorkspace.teammateWorkspaceTags),
    };
    await dispatch(updateWorkspaceAsyncAction(updatedVisualWorkspace));
  };
}

/** Hides a custom view in a team workspace. */
function hideCustomViewInTeamWorkspaceAsyncAction(
  tagId: TagModel['id'],
  teamId: TeamModel['id'],
): ThunkAsyncAction {
  return async (dispatch, getState) => {
    const visualWorkspace = getUserVisualWorkspace(getState());
    const {updatedVisualWorkspace} = toggleVisualWorkspaceTeamCustomView(visualWorkspace, {
      id: tagId,
      teamId,
    });
    await dispatch(updateWorkspaceAsyncAction(updatedVisualWorkspace));
  };
}

/** Shows a custom view's visibility in a team workspace. */
function showCustomViewInTeamWorkspaceAsyncAction(
  tagId: TagModel['id'],
  teamId: TeamModel['id'],
): ThunkAsyncAction {
  return async (dispatch, getState) => {
    const visualWorkspace = getUserVisualWorkspace(getState());
    const updatedVisualWorkspace = {
      ...visualWorkspace,
      teams: visualWorkspace.teams.map((teamData) => {
        if (teamData.teamId !== teamId) {
          return teamData;
        }

        return {
          ...teamData,
          customViews: showVisualWorkspaceTag(tagId, teamData.customViews),
        };
      }),
    };
    await dispatch(updateWorkspaceAsyncAction(updatedVisualWorkspace));
  };
}

/**
 * Hides a tag in a workspace.
 * This tag may represent either
 * - a private or team tag in the personal workspace or
 * - a team custom view in a team workspace.
 */
export function hideWorkspaceTagAsyncAction(
  tagId: number,
  isView: boolean,
  namespace?: string,
): ThunkAsyncAction {
  return async (dispatch, getState) => {
    if (!namespace) {
      return undefined;
    }

    // Currently, only team custom views are put in the team -> tags section of the workspace.
    // Team tags are added to my_inbox -> tags so that we can correctly sort the combined list of
    // private and team tags in the personal workspace.
    const maybeTeamId = parseTeamNamespace(namespace);
    if (maybeTeamId && isView) {
      await dispatch(hideCustomViewInTeamWorkspaceAsyncAction(tagId, maybeTeamId));
    } else if (!isView) {
      await dispatch(hideTagInPersonalWorkspaceAsyncAction(tagId));
    }
    // We should never reach this point.
    else {
      reportToBugsnag(new Error(`Unable to hide tag in workspace`), {
        metadata: {
          tagId,
          isView,
          namespace,
          maybeTeamId,
        },
      });
    }

    return undefined;
  };
}

/**
 * Shows a tag in a workspace.
 * This tag may represent either
 * - a private or team tag in the personal workspace or
 * - a team custom view in a team workspace.
 */
export function showWorkspaceTagAsyncAction(
  tagId: number,
  isView: boolean,
  namespace?: string,
): ThunkAsyncAction {
  return async (dispatch, getState) => {
    if (!namespace) {
      return undefined;
    }

    // Currently, only team custom views are put in the team -> tags section of the workspace.
    // Team tags are added to my_inbox -> tags so that we can correctly sort the combined list of
    // private and team tags in the personal workspace.
    const maybeTeamId = parseTeamNamespace(namespace);
    if (maybeTeamId && isView) {
      await dispatch(showCustomViewInTeamWorkspaceAsyncAction(tagId, maybeTeamId));
    } else if (!isView) {
      await dispatch(showTagInPersonalWorkspaceAsyncAction(tagId));
    }
    // We should never reach this point.
    else {
      reportToBugsnag(new Error(`Unable to show tag in workspace`), {
        metadata: {
          tagId,
          isView,
          namespace,
          maybeTeamId,
        },
      });
    }

    return undefined;
  };
}

/** Shows a lens in the lenses ("Saved views") workspace. */
export function showWorkspaceLensAsyncAction(lensId: number): ThunkAsyncAction {
  return async (dispatch, getState) => {
    const visualWorkspace = getUserVisualWorkspace(getState());
    const {updatedVisualWorkspace} = showVisualWorkspaceLens(visualWorkspace, lensId);
    await dispatch(updateWorkspaceAsyncAction(updatedVisualWorkspace));
  };
}

/** Hides a lens in the lenses ("Saved views") workspace. */
export function hideWorkspaceLensAsyncAction(lensId: number): ThunkAsyncAction {
  return async (dispatch, getState) => {
    const visualWorkspace = getUserVisualWorkspace(getState());
    const {updatedVisualWorkspace} = hideVisualWorkspaceLens(visualWorkspace, lensId);
    await dispatch(updateWorkspaceAsyncAction(updatedVisualWorkspace));
  };
}

/** Hides a teammate in the assigned teammates workspace. */
export function hideTeammateInAssignedTeammatesWorkspaceAsyncAction(teammateId: number): ThunkAsyncAction {
  return async (dispatch, getState) => {
    const visualWorkspace = getUserVisualWorkspace(getState());
    const {updatedVisualWorkspace} = toggleVisualWorkspaceTeammate(visualWorkspace, teammateId);
    await dispatch(updateWorkspaceAsyncAction(updatedVisualWorkspace));
  };
}

/**
 * Updates the user's workspace based upon a VisualWorkspaceModel.
 *
 * {@see serializeWorkspace} for how the model is serialized (particularly labels/folders) before being sent to the API.
 */
export function updateWorkspaceAsyncAction(visualWorkspace: VisualWorkspaceModel): ThunkAsyncAction {
  return async (dispatch, getState) => {
    const state = getState();
    const teams = getTeams(state);

    const updateWorkspace: WorkspaceUpdater = (existingWorkspace) => {
      // Personal workspace: Root folders (labels only).
      const updatedRootFolders = chain(visualWorkspace.rootFolders)
        .map((visualWorkspaceRootFolder): WorkspaceFolderLabelV2Model | null =>
          RWorkspaceFolderRootLabels.is(visualWorkspaceRootFolder.id)
            ? {
                name: visualWorkspaceRootFolder.id,
                type: WorkspaceItemTypesEnum.LABEL,
                last_viewed: undefined,
                is_hidden: !visualWorkspaceRootFolder.isSelected,
              }
            : null,
        )
        .compact()
        .value();

      // Personal workspace: Nested folders (mixture of labels and inboxes).
      const updatedNestedFolders = visualWorkspace.nestedFolders.map(
        (visualWorkspaceNestedFolder): WorkspaceFolderNestedItemV2 => {
          if (visualWorkspaceNestedFolder.type === WorkspaceItemTypesEnum.INBOX) {
            return {
              id: visualWorkspaceNestedFolder.id,
              type: WorkspaceItemTypesEnum.INBOX,
              last_viewed: undefined,
              is_hidden: !visualWorkspaceNestedFolder.isSelected,
            };
          }
          return {
            name: visualWorkspaceNestedFolder.id,
            type: WorkspaceItemTypesEnum.LABEL,
            last_viewed: undefined,
            is_hidden: !visualWorkspaceNestedFolder.isSelected,
          };
        },
      );

      // Personal workspace: Tags.
      const updatedTeammateWorkspaceTags = visualWorkspace.teammateWorkspaceTags
        .filter(({isSelected}) => isSelected)
        .map(({id}) => ({id, last_viewed: undefined}));

      // Lenses.
      const updatedLenses = visualWorkspace.lenses
        .filter(({isSelected}) => isSelected)
        .map(({id}) => ({id, last_viewed: undefined}));

      // Teammate delegations.
      const updatedDelegations = visualWorkspace.delegations
        .filter(({isSelected}) => isSelected)
        .map(({id}) => ({id, last_viewed: undefined}));

      // Teammate conversations.
      const updatedTeammates = visualWorkspace.teammates
        .filter(({isSelected}) => isSelected)
        .map(({id}) => ({id, last_viewed: undefined}));

      // Teams workspaces (team inboxes and views).
      const updatedTeams = visualWorkspace.teams.reduce(
        (result, visualWorkspaceTeam) => {
          const {teamId, inboxes, customViews} = visualWorkspaceTeam;
          const team = teams[teamId];
          if (!team) {
            return result;
          }

          return {
            ...result,
            [teamId]: {
              inboxes: inboxes
                .filter(({isSelected}) => isSelected)
                .map(({id}) => ({id, last_viewed: undefined})),
              tags: customViews
                .filter(({isSelected}) => isSelected)
                .map(({id}) => ({id, last_viewed: undefined})),
            },
          };
        },
        {...existingWorkspace.value.teams_v2},
      );

      return {
        ...existingWorkspace,
        value: {
          ...existingWorkspace.value,
          [WorkspaceSectionTypesEnum.MY_INBOX_V2]: {
            ...existingWorkspace.value[WorkspaceSectionTypesEnum.MY_INBOX_V2],
            root_folders: updatedRootFolders,
            nested_folders: updatedNestedFolders,
            tags: updatedTeammateWorkspaceTags,
          },
          [WorkspaceSectionTypesEnum.LENSES]: updatedLenses,
          [WorkspaceSectionTypesEnum.TEAMS_V2]: updatedTeams,
          [WorkspaceSectionTypesEnum.TEAMMATE_DELEGATIONS_V2]: updatedDelegations,
          [WorkspaceSectionTypesEnum.TEAMMATE_CONVERSATIONS_V2]: updatedTeammates,
        },
      };
    };

    await dispatch(workspaceUpdatedAsyncAction(updateWorkspace));
  };
}

/*
 * Helpers.
 */

/**
 * Enforces that a tag is displayed in a workspace.
 * If it is already displayed (in the provided array and selected), no change is made.
 * If it is in the workspace but hidden (in the provided array and not selected), then its item is removed and one is added at the end of the array.
 * If it is not in the workspace (not in the provided array), then an item is added at the end of the array.
 */
function showVisualWorkspaceTag(
  tagId: TagModel['id'],
  visualWorkspaceTags: ReadonlyArray<VisualWorkspaceTag>,
): ReadonlyArray<VisualWorkspaceTag> {
  const isTagAlreadyShown = visualWorkspaceTags.some((item) => item.id === tagId && item.isSelected);
  return isTagAlreadyShown
    ? visualWorkspaceTags
    : [
        ...visualWorkspaceTags.filter((item) => item.id !== tagId),
        {
          id: tagId,
          isSelected: true,
          isRecentlyViewed: visualWorkspaceTags.find((workspaceTag) => workspaceTag.id === tagId)
            ?.isRecentlyViewed,
        },
      ];
}
